import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import { useState } from "react"
import type { RecordTimelineEntry } from "../../services/ingestionApi"
import { useRecordTimeline } from "../../hooks/queries/useIngestion"

interface RecordTimelineProps {
  studyId: string
  tableName: string
  primaryKeyValue: string
}

const statusConfig: Record<string, { type: "success" | "info" | "error"; label: string }> = {
  INSERTED: { type: "success", label: "Inserted" },
  UPDATED: { type: "info", label: "Updated" },
  QUARANTINED: { type: "error", label: "Quarantined" },
}

const DEFAULT_STATUS = statusConfig.INSERTED

const RecordTimeline = ({ studyId, tableName, primaryKeyValue }: RecordTimelineProps) => {
  const {
    data: entries = [],
    isLoading: loading,
    error: queryError,
  } = useRecordTimeline(studyId, tableName, primaryKeyValue)

  const rawError = queryError instanceof Error ? queryError.message : null
  const error = rawError
    ? rawError.includes("timeout") || rawError.includes("504") || rawError.includes("Query timeout")
      ? "Query timed out. The record history query is taking too long. Try again or select a different record."
      : rawError
    : null

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "-"
    const d = new Date(timestamp)
    if (Number.isNaN(d.getTime())) return "-"
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return ""
    const d = new Date(timestamp)
    if (Number.isNaN(d.getTime())) return ""
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  }

  if (loading) {
    return (
      <Box textAlign="center" padding="l" color="text-status-inactive">
        <Spinner /> Loading record timeline...
      </Box>
    )
  }

  if (error) {
    return <Alert type="error">Error loading timeline: {error}</Alert>
  }

  if (entries.length === 0) {
    return (
      <Box textAlign="center" padding="l" color="inherit">
        No history found for record {primaryKeyValue}
      </Box>
    )
  }

  // Group entries by runId to show duplicates together
  const groupedEntries: {
    runId: string
    timestamp: string | null
    fileName?: string
    entries: RecordTimelineEntry[]
  }[] = []
  entries.forEach((entry) => {
    const existing = groupedEntries.find((g) => g.runId === entry.runId)
    if (existing) {
      existing.entries.push(entry)
    } else {
      groupedEntries.push({
        runId: entry.runId,
        timestamp: entry.timestamp,
        fileName: entry.fileName,
        entries: [entry],
      })
    }
  })

  return (
    <SpaceBetween size="l">
      <Header
        variant="h3"
        description={`${tableName} • ${entries.length} event${
          entries.length !== 1 ? "s" : ""
        } across ${groupedEntries.length} upload${groupedEntries.length !== 1 ? "s" : ""}`}
      >
        Record Timeline: {primaryKeyValue}
      </Header>

      <SpaceBetween size="m">
        {groupedEntries.map((group) => {
          const hasMultiple = group.entries.length > 1
          const allQuarantined = group.entries.every((e) => e.status === "QUARANTINED")

          return (
            <SpaceBetween key={group.runId} size="xs">
              {/* Upload header */}
              <SpaceBetween direction="horizontal" size="s">
                <StatusIndicator type={allQuarantined ? "error" : "success"}>
                  {formatDate(group.timestamp)}, {formatTime(group.timestamp)}
                </StatusIndicator>
                {group.fileName && (
                  <Box color="text-status-inactive" display="inline">
                    {group.fileName}
                  </Box>
                )}
                {hasMultiple && (
                  <StatusIndicator type="warning">
                    {group.entries.length} duplicate records
                  </StatusIndicator>
                )}
              </SpaceBetween>

              {/* Entries in this group. */}
              <div
                style={{
                  marginLeft: "6px",
                  paddingLeft: "18px",
                  borderLeft: `2px solid ${hasMultiple ? "#fbbf24" : "#e9ebed"}`,
                }}
              >
                <SpaceBetween size="xs">
                  {group.entries.map((entry, entryIndex) => (
                    <EntryCard key={entryIndex} entry={entry} />
                  ))}
                </SpaceBetween>
              </div>
            </SpaceBetween>
          )
        })}
      </SpaceBetween>
    </SpaceBetween>
  )
}

const PREVIEW_COUNT = 6

function EntryCard({ entry }: { entry: RecordTimelineEntry }) {
  const [expanded, setExpanded] = useState(false)
  const { type, label } = statusConfig[entry.status] ?? DEFAULT_STATUS
  const isError = entry.status === "QUARANTINED"

  const visibleFields = Object.entries(entry.values).filter(
    ([key]) => !key.startsWith("DataQuality") && !key.startsWith("_ctm_"),
  )
  const hasMore = visibleFields.length > PREVIEW_COUNT
  const displayFields = expanded ? visibleFields : visibleFields.slice(0, PREVIEW_COUNT)

  return (
    <div
      style={{
        padding: "12px",
        background: isError ? "#fdf3f3" : "#f2fcf3",
        borderRadius: "8px",
        border: `1px solid ${isError ? "#fecaca" : "#bbf7d0"}`,
      }}
    >
      <SpaceBetween size="xs">
        <SpaceBetween direction="horizontal" size="xs">
          <StatusIndicator type={type}>{label}</StatusIndicator>
          {entry.reason && (
            <Box color="text-status-error" display="inline">
              — {entry.reason}
            </Box>
          )}
        </SpaceBetween>
        <ColumnLayout columns={3} variant="text-grid">
          {displayFields.map(([key, value]) => (
            <div key={key}>
              <Box variant="awsui-key-label">{key}</Box>
              <Box>
                {value === null
                  ? "NULL"
                  : typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value as string | number | boolean)}
              </Box>
            </div>
          ))}
        </ColumnLayout>
        {hasMore && (
          <Button variant="inline-link" onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? "Show less" : `Show all ${visibleFields.length} fields`}
          </Button>
        )}
      </SpaceBetween>
    </div>
  )
}

export default RecordTimeline
